import { IAnalyticsServiceConfiguration } from '..'
import { IDate, ITitle } from '../../../domain/conf/page'
import { Utils } from '../../../utils/common'
import { DateFormat, DateUtils } from '../../../utils/date'
import { KlangooUtils } from '../../../utils/klangoo'
import { IKlangooNLPConfig } from '../../common/nlp/klangoo-config'
import { PageType } from '../../common/page-types'
import { AnalyticsMetaKeys as key } from './meta-keys'

const NLP_ENTITY_SCORE_THRESHOLD = .25
const NLP_ENTITY_RANK = ['R', 'VR']

export interface IAnalyticsMetaFormatter {
    meta(conf: IAnalyticsServiceConfiguration): any
}

export class DefaultAnalyticsFormatter implements IAnalyticsMetaFormatter {
    public static dateFormat = DateFormat.YYYY_MM_DD
    public static timeFormat = DateFormat.HH_MM_SS
    public static supportedPageTypes = [PageType.CATEGORY, PageType.INDEX, PageType.STORY]

    public meta(conf: IAnalyticsServiceConfiguration) {
        const meta: any = {
            // user
            [key.USER_LOGGEDIN]: String(!!conf.profile.email),
            // ads
            [key.AD_SITE]: conf.adSite,
            // analytics
            [key.BRAND]: conf.brand,
            [key.DIVISION]: conf.division,
            [key.DOMAIN]: conf.domain,
            [key.VENDOR]: conf.vendor,
            [key.DEBUG]: conf.debug,
            // page
            [key.TYPE]: conf.page.type,
            [key.PAYWALL]: conf.page.paywall
        }

        if (conf.page.articleId) {
            meta[key.ARTICLE_ID] = conf.page.articleId
        }
        if (conf.page.originId) {
            meta[key.ORIGINAL_ID] = conf.page.originId
        }
        if (conf.page.dist) {
            meta[key.DISTRIBUTOR] = conf.page.dist
        }
        if (conf.page.error) {
            meta[key.ERROR] = String(conf.page.error).match(/20[01]/) ? String(false) : String(true)
        } else {
            meta[key.ERROR] = String(false)
        }
        if (conf.page.excerpt) {
            meta[key.EXCERPT] = conf.page.excerpt
        }
        if (String(conf.page.isOrigin)) {
            meta[key.IS_ORIGINAL] = String(conf.page.isOrigin)
        }
        if (conf.page.video && String(conf.page.video.inPage)) {
            meta[key.VID_IN_PAGE] = String(conf.page.video.inPage)
        }
        if (conf.page.wc) {
            meta[key.WRD_CNT] = String(conf.page.wc)
        }
        if (conf.page.authors && conf.page.authors.length > 0) {
            meta[key.AUTHORS] = conf.page.authors.join(', ')
        }
        if (conf.page.sponsor) {
            meta[key.SPONSOR] = conf.page.sponsor
        }
        return {
            ...meta,
            ...this.tag(conf.page),
            ...this.title(conf.page.title),
            ...this.category(conf.page.ancestry),
            ...this.dateFormat(conf.page.date),
            ...this.klangoo(conf.page.klangoo)
        }
    }

    // tag and kw
    public tag(data: any): object {
        const meta: any = {}
        if (data.kw) {
            meta[key.NEWS_KEYWRD] = data.kw.join(', ')
        }
        if (data.tags) {
            meta[key.META_TAG] = data.tags.join(', ')
        }
        if (data.slug) {
            meta[key.SLUG] = String(data.slug)
        }
        return meta
    }

    // Title
    public title(data: ITitle): object {
        const meta: any = {}
        if (data) {
            if (data.main) {
                meta[key.TITLE] = Utils.decodeHtmlEntity(data.main)
            }
            if (data.alt) {
                meta[key.HEADLINE] = Utils.decodeHtmlEntity(data.alt)
            }
        }
        return meta
    }

    // Dates
    public dateFormat(data: IDate): object {
        const meta: any = {}
        if (data) {
            if (data.pub) {
                meta[key.PUB_DATE] = DateUtils.format(
                    data.pub, DefaultAnalyticsFormatter.dateFormat)
                meta[key.PUB_TIME] = DateUtils.format(
                    data.pub, DefaultAnalyticsFormatter.timeFormat)
            }
            if (data.mod) {
                meta[key.MOD_DATE] = DateUtils.format(
                    data.mod, DefaultAnalyticsFormatter.dateFormat)
                meta[key.MOD_TIME] = DateUtils.format(
                    data.mod, DefaultAnalyticsFormatter.timeFormat)
            }
        }
        return meta
    }
    // Category
    public category(data: string[]): object {
        const meta: any = {}
        if (data) {
            if (data.length > 0) {
                meta[key.CATEGORY] = data.shift()
            }
            if (data.length > 0) {
                meta[key.SUB_SEC] = meta[key.CATEGORY] + ' > ' + data.shift()
            }
            if (data.length > 0) {
                meta[key.SUB_SEC_2] = meta[key.SUB_SEC] + ' > ' + data.shift()
            }
            if (data.length > 0) {
                meta[key.SUB_SEC_3] = meta[key.SUB_SEC_2] + ' > ' + data.shift()
            }
        }
        return meta
    }

    // Kalngoo
    public klangoo(data: IKlangooNLPConfig): object {
        const meta: any = {}
        if (data && data.entities) {
            meta[key.KLNGO_ENTITY] =
                [...new Set(
                    [...KlangooUtils.stringifyFilteredEntities(data.entities,
                        NLP_ENTITY_RANK, NLP_ENTITY_SCORE_THRESHOLD),
                    ...KlangooUtils.stringifyFilteredKeytopics(data.keyTopics,
                        NLP_ENTITY_SCORE_THRESHOLD),
                    ...KlangooUtils.stringifyFilteredCategories(data.categories,
                        NLP_ENTITY_SCORE_THRESHOLD)]
                )].join(', ')
        }
        if (data && data.categories) {
            meta[key.KLNGO_CATEGORY] = KlangooUtils.stringifyFilteredCategories(data.categories,
                NLP_ENTITY_SCORE_THRESHOLD).join(', ')
        }
        return meta
    }
}
